import { initializeApp, deleteApp, getApps } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
} from 'firebase/auth';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { createSubscription, normalizeEmail } from './subscriptions';
import { notifyStudentEnrolled } from './notifications';

const SECONDARY_APP_NAME = 'SBAStudentProvisioner';

/**
 * Create a Firebase Auth student + profile without signing out the admin.
 * Uses a secondary Firebase app instance so the admin session stays intact.
 */
export async function createStudentAccount({
  studentName,
  email,
  password,
  planId = '1-month',
  planDays,
  planLabel,
  createdBy = '',
  payment = null,
}) {
  const normalizedEmail = normalizeEmail(email);
  const name = (studentName || '').trim();

  if (!normalizedEmail) throw new Error('A valid student email is required.');
  if (!name) throw new Error('Student name is required.');
  if (!password || password.length < 6) {
    throw new Error('Password must be at least 6 characters.');
  }

  const primary = getApps().find((a) => a.name === '[DEFAULT]');
  if (!primary) throw new Error('Firebase is not initialized.');

  const existingSecondary = getApps().find((a) => a.name === SECONDARY_APP_NAME);
  if (existingSecondary) {
    await deleteApp(existingSecondary);
  }

  const secondaryApp = initializeApp(primary.options, SECONDARY_APP_NAME);
  const secondaryAuth = getAuth(secondaryApp);

  let uid = '';

  try {
    const cred = await createUserWithEmailAndPassword(
      secondaryAuth,
      normalizedEmail,
      password
    );
    uid = cred.user.uid;
    await updateProfile(cred.user, { displayName: name });

    await setDoc(doc(db, 'users', uid), {
      email: normalizedEmail,
      emailLower: normalizedEmail,
      displayName: name,
      role: 'student',
      createdAt: serverTimestamp(),
      provisionedBy: createdBy || '',
    });

    const subscription = await createSubscription({
      studentName: name,
      email: normalizedEmail,
      userId: uid,
      planId,
      planDays,
      planLabel,
      createdBy,
      payment,
    });

    if (subscription.registrationNumber) {
      await updateDoc(doc(db, 'users', uid), {
        registrationNumber: subscription.registrationNumber,
        registrationAssignedAt: serverTimestamp(),
      });
    }

    await signOut(secondaryAuth);

    await notifyStudentEnrolled({
      userId: uid,
      registrationNumber: subscription.registrationNumber || '',
      planLabel,
    }).catch(console.warn);

    return {
      uid,
      email: normalizedEmail,
      password,
      displayName: name,
      registrationNumber: subscription.registrationNumber || '',
      paymentId: subscription.paymentId || null,
      payment: subscription.payment || null,
    };
  } catch (err) {
    try {
      await signOut(secondaryAuth);
    } catch {
      /* ignore */
    }
    throw err;
  } finally {
    try {
      await deleteApp(secondaryApp);
    } catch {
      /* ignore */
    }
  }
}

export function getCreateAccountErrorMessage(err) {
  const code = err?.code || '';
  switch (code) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Create a subscription for that email only, or use a different email.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/operation-not-allowed':
      return 'Email/password sign-in is not enabled in Firebase Console.';
    default:
      return err?.message || 'Failed to create student account.';
  }
}
